import React, { useState, useEffect } from 'react'
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation,
  useMatch
} from 'react-router-dom'
import { Layout, Spin, App as AntApp } from 'antd'
import { useAuth, AuthProvider } from './hooks/AuthContext'
import Sidebar from './components/Sidebar'
import Appointments from './pages/Appointments'
import Products from './pages/ProductPage'
import Customers from './pages/Customers'
import CustomerDetail from './pages/CustomerDetail'
import CustomerServicePhotosPage from './pages/CustomerServicePhotosPage'
import CustomerGroupsPage from './pages/CustomerGroupsPage'
import CustomerGroupDetailPage from './pages/CustomerGroupDetailPage'
import CustomerGroupFormPage from './pages/CustomerGroupFormPage'
import MembershipTierSettingsPage from './pages/MembershipTierSettingsPage'
import NavBar from './components/NavBar'
import Login from './pages/LoginPage'
import RegisterPage from './pages/RegisterPage'
import BookingTable from './components/BookingTable'
import BookingDetailPageWrapper from './pages/BookingDetailPage'
import PendingAssignmentsPage from './pages/PendingAssignmentsPage'
import SettingsPage from './pages/SettingsPage'
import EmployeesPage from './pages/EmployeesPage'
import EmployeeSalarySettingsPage from './pages/EmployeeSalarySettingsPage'
import EmployeeCommissionSettingsPage from './pages/EmployeeCommissionSettingsPage'
import ValueAddedCommissionSettingsPage from './pages/ValueAddedCommissionSettingsPage'
import ProductSalesCommissionSettingsPage from './pages/ProductSalesCommissionSettingsPage'
import ClassTicketCommissionSettingsPage from './pages/ClassTicketCommissionSettingsPage'
import ServiceCommissionSettingsPage from './pages/ServiceCommissionSettingsPage'
import AllowanceSettingsPage from './pages/AllowanceSettingsPage'
import MonthlySalarySummaryPage from './pages/MonthlySalarySummaryPage'
import EmployeeDetail from './components/EmployeeDetail'
import ProductDetailPage from './pages/ProductDetailPage'
import DashboardPage from './pages/DashboardPage'
import CouponPage from './pages/CouponPage'
import AllNotifications from './pages/AllNotifications'
import Applicants from './components/Applicants'
import RecruitmentDashboard from './components/RecruitmentDashboard'
import RecruitmentList from './components/RecruitmentList'
import AdditionalServicesPage from './pages/AdditionalServicesPage'
import StorePage from './pages/StorePage'
import AddProductPage from './pages/AddProductPage'
import ShopSetting from './pages/ShopSetting'
import InventoryProductsPage from './pages/InventoryProductsPage'
import AddInventoryProductPage from './pages/AddInventoryProductPage'
import InventoryPurchasePage from './pages/InventoryPurchasePage'
import InventoryPurchaseRecordsPage from './pages/InventoryPurchaseRecordsPage'
import InventoryUsagePage from './pages/InventoryUsagePage'
import InventoryPublicUsePage from './pages/InventoryPublicUsePage'
import OrdersPage from './pages/OrdersPage'
import ValueAddedManagementPage from './pages/ValueAddedManagementPage'
import ClassTicketManagementPage from './pages/ClassTicketManagementPage'
import SchedulePage from './pages/SchedulePage'
import ConsentFormManagementPage from './pages/ConsentFormManagementPage'
import ReviewManagementPage from './pages/ReviewManagementPage'
import CheckoutManagementPage from './pages/CheckoutManagementPage'
import RevenueRecordsPage from './pages/RevenueRecordsPage'
import AdminLoginPage from './pages/admin/AdminLoginPage'
import AdminPrivateRoute from './components/AdminPrivateRoute'
import AdminDashboard from './pages/admin/AdminDashboard'
import StoreApplications from './pages/admin/StoreApplications'
import ActiveStoresAdmin from './pages/admin/ActiveStoresAdmin'
import AdminLayout from './components/layout/AdminLayout'
import UsersList from './pages/admin/UsersList'
import BusinessUsersList from './pages/admin/BusinessUsersList'
import BookingsList from './pages/admin/BookingsList'
import ChangeBusinessPasswordPage from './pages/admin/ChangeBusinessPasswordPage'
import PaymentPage from './pages/PaymentPage'
import PaymentResult from './pages/PaymentResult'
import PaymentApplicationsPage from './pages/admin/PaymentApplicationsPage'
import PaymentApplicationDetail from './pages/admin/PaymentApplicationDetail'
import ArticlePermissionManagement from './pages/admin/ArticlePermissionManagement'
import CreditPaymentsPage from './pages/admin/CreditPaymentsPage'
import PlatformAdminsPage from './pages/admin/PlatformAdminsPage'
import SubscriptionLoginPage from './pages/SubscriptionLoginPage'
import SubscriptionListPage from './pages/SubscriptionListPage'
import SubscriptionInfoPage from './pages/SubscriptionInfoPage'
import SubscriptionApplicationFormPage from './pages/SubscriptionApplicationFormPage'
import SubscriptionApplicationsPage from './pages/SubscriptionApplicationsPage'
import RevisitReminderPage from './pages/RevisitReminderPage'
import RevisitReminderConfigPage from './pages/RevisitReminderConfigPage'
import AnnouncementsPage from './pages/AnnouncementsPage'
import AdminChangelogPage from './pages/admin/ChangelogPage'
import KpiReportPage from './pages/admin/KpiReportPage'
import FunnelReportPage from './pages/admin/FunnelReportPage'
import KpiStatsPage from './pages/admin/reports/KpiStatsPage'
import PaymentRecordsPage from './pages/PaymentRecordsPage'
import AppTourLauncher from './components/AppTourLauncher'
import SelectBusinessPage from './pages/SelectBusinessPage'
// 線上商店模組
import ShopOrdersPage from './pages/shop/ShopOrdersPage'
import ShopPickupPage from './pages/shop/ShopPickupPage'
import ShopProductsPage from './pages/shop/ShopProductsPage'
import ShopSettingsPage from './pages/shop/ShopSettingsPage'

const { Content } = Layout

const PrivateRoute = ({ children }) => {
  // AuthProvider 掛載時已經驗證過身分，這裡只要等它的結果就好。
  // 原本每次進入受保護路由都會再打一次 check-business-status，
  // 而那支會連帶串出 employees/role 與 business_B 兩支，等於整輪多 3 支 API。
  const { user, authInitializing } = useAuth()
  const location = useLocation()

  if (authInitializing) {
    return (
      <Spin size='large' style={{ marginTop: '20px', marginLeft: '50%' }} />
    )
  }

  if (!user) {
    // 重定向到登錄頁面，並傳遞當前位置作為 state
    return <Navigate to='/login' state={{ from: location.pathname }} replace />
  }

  return children
}

const App = () => {
  // 與 Sidebar 同步：≤1439px 時預設收合
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    window.innerWidth <= 1439
  )
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 820)
  // 中等寬度：821 ~ 1280px（平板橫向 / 小筆電）— 此時左右給更小的 margin，
  // 避免內容被 60px margin 擠到中間留太多空白
  const [isMediumWidth, setIsMediumWidth] = useState(
    window.innerWidth > 820 && window.innerWidth <= 1280
  )

  useEffect(() => {
    const handleResize = () => {
      const w = window.innerWidth
      setIsMobile(w <= 820)
      setIsMediumWidth(w > 820 && w <= 1280)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const handleSidebarCollapse = (collapsed) => {
    setSidebarCollapsed(collapsed)
  }

  const CUSTOMER_DETAIL_RESERVED = new Set([
    'groups',
    'membership-tiers',
    'revisit-reminders'
  ])

  const ContentWrapper = ({ children }) => {
    const customerDetailMatch = useMatch({
      path: '/customers/:customerId',
      end: true
    })
    const isCustomerDetailPage = Boolean(
      customerDetailMatch?.params?.customerId &&
        !CUSTOMER_DETAIL_RESERVED.has(customerDetailMatch.params.customerId)
    )
    const bookingDetailMatch = useMatch({ path: '/bookings/:id', end: true })
    const isBookingDetailPage = Boolean(bookingDetailMatch?.params?.id)

    const isFullBleedGrayPage = isCustomerDetailPage || isBookingDetailPage

    // 響應式 margin：
    //   手機（≤820）：頂部留 55px navbar、左右貼齊
    //   中等寬度（821 ~ 1280，平板橫向 / 小筆電）：左右 16px，避免內容被 60px margin 擠到中間留太多空白
    //   大桌機（>1280）：保留原本 60px 寬鬆 margin，視覺更舒展
    const contentMargin = isMobile
      ? '55px 0px 0px 0px'
      : isMediumWidth
        ? '5px 16px 0px'
        : '5px 60px 0px'

    const contentStyle = isFullBleedGrayPage
      ? {
          margin: contentMargin,
          borderRadius: 0,
          padding: 0,
          background: '#f5f5f5',
          minHeight: isMobile ? 'calc(100vh - 55px)' : 'calc(100vh - 8px)'
        }
      : {
          margin: contentMargin,
          borderRadius: '12px',
          padding: isMobile ? 5 : isMediumWidth ? 16 : 24,
          background: '#fff'
        }

    return <Content style={contentStyle}>{children}</Content>
  }

  return (
    <AntApp>
      <AuthProvider>
        <Router>
          <Routes>
            {/* 公開路由 */}
            <Route path='/login' element={<Login />} />
            <Route path='/admin/login' element={<AdminLoginPage />} />
            <Route path='/register' element={<RegisterPage />} />
            <Route
              path='/payment/quick-payment/:orderSn'
              element={<PaymentPage />}
            />
            <Route
              path='/payment/quick-payment/result'
              element={<PaymentResult />}
            />
            {/* 訂閱資訊路由 */}
            <Route
              path='/subscription/login'
              element={<SubscriptionLoginPage />}
            />
            <Route
              path='/subscription/list'
              element={<SubscriptionListPage />}
            />
            <Route
              path='/subscription/info'
              element={<SubscriptionInfoPage />}
            />
            <Route
              path='/subscription/applications'
              element={<SubscriptionApplicationsPage />}
            />
            {/* 公開的訂閱申請表單頁面 */}
            <Route
              path='/subscription/apply'
              element={<SubscriptionApplicationFormPage />}
            />

            {/* 管理員路由 */}
            <Route
              path='/admin/*'
              element={
                <AdminPrivateRoute>
                  <AdminLayout>
                    <Routes>
                      <Route path='/dashboard' element={<AdminDashboard />} />
                      <Route
                        path='/stores/active'
                        element={<ActiveStoresAdmin />}
                      />
                      <Route path='/stores' element={<StoreApplications />} />
                      <Route path='/users' element={<UsersList />} />
                      <Route
                        path='/business-users'
                        element={<BusinessUsersList />}
                      />
                      <Route path='/bookings' element={<BookingsList />} />
                      <Route
                        path='/payment-applications'
                        element={<PaymentApplicationsPage />}
                      />
                      <Route
                        path='/payment-applications/:id'
                        element={<PaymentApplicationDetail />}
                      />
                      <Route
                        path='/change-password'
                        element={<ChangeBusinessPasswordPage />}
                      />
                      <Route
                        path='/article-permission'
                        element={<ArticlePermissionManagement />}
                      />
                      <Route
                        path='/credit-payments'
                        element={<CreditPaymentsPage />}
                      />
                      <Route
                        path='/platform-admins'
                        element={<PlatformAdminsPage />}
                      />
                      <Route
                        path='/changelog'
                        element={<AdminChangelogPage />}
                      />
                      <Route
                        path='/kpi-report'
                        element={<KpiReportPage />}
                      />
                      <Route
                        path='/funnel-report'
                        element={<FunnelReportPage />}
                      />
                      {/* 數據報表模組 */}
                      <Route
                        path='/reports/kpi'
                        element={<KpiStatsPage />}
                      />
                    </Routes>
                  </AdminLayout>
                </AdminPrivateRoute>
              }
            />

            {/* 商家後台路由：登入後直接進主畫面，不強制 onboarding */}
            <Route
              path='/*'
              element={
                <PrivateRoute>
                  <Layout style={{ minHeight: '100vh' }}>
                    <Sidebar onCollapse={handleSidebarCollapse} />
                    <Layout
                      style={{
                        marginLeft: isMobile ? 0 : sidebarCollapsed ? 80 : 240,
                        transition: 'all 0.2s'
                      }}
                    >
                      <AppTourLauncher />
                      <NavBar />
                      <ContentWrapper>
                        <Routes>
                          <Route
                            path='/'
                            element={<Navigate to='/appointments' replace />}
                          />
                          <Route
                            path='/select-business'
                            element={<SelectBusinessPage />}
                          />
                          <Route
                            path='/dashboard'
                            element={<DashboardPage />}
                          />
                          <Route
                            path='/dashboard/revenue-records'
                            element={<RevenueRecordsPage />}
                          />
                          <Route
                            path='/appointments'
                            element={<Appointments />}
                          />
                          <Route
                            path='/booking-detail'
                            element={<BookingTable />}
                          />
                          <Route
                            path='/bookings/pending-assignment'
                            element={<PendingAssignmentsPage />}
                          />
                          <Route
                            path='/bookings/:id'
                            element={<BookingDetailPageWrapper />}
                          />
                          <Route path='/products' element={<Products />} />
                          <Route
                            path='products/:businessId/:productId'
                            element={<ProductDetailPage />}
                          />
                          <Route path='/customers' element={<Customers />} />
                          <Route
                            path='/customers/membership-tiers'
                            element={<MembershipTierSettingsPage />}
                          />
                          <Route
                            path='/customers/revisit-reminders'
                            element={<RevisitReminderPage />}
                          />
                          <Route
                            path='/customers/revisit-reminders/new'
                            element={<RevisitReminderConfigPage />}
                          />
                          <Route
                            path='/customers/revisit-reminders/:id'
                            element={<RevisitReminderConfigPage />}
                          />
                          <Route
                            path='/customers/groups'
                            element={<CustomerGroupsPage />}
                          />
                          <Route
                            path='/customers/groups/new'
                            element={<CustomerGroupFormPage />}
                          />
                          <Route
                            path='/customers/groups/:groupId/edit'
                            element={<CustomerGroupFormPage />}
                          />
                          <Route
                            path='/customers/groups/:groupId'
                            element={<CustomerGroupDetailPage />}
                          />
                          <Route
                            path='/customers/:customerId'
                            element={<CustomerDetail />}
                          />
                          {/* 顧客的施作照片紀錄（完整時間軸）；顧客詳情只放最近 3 次預覽 */}
                          <Route
                            path='/customers/:customerId/service-photos'
                            element={<CustomerServicePhotosPage />}
                          />
                          <Route
                            path='/employees'
                            element={<EmployeesPage />}
                          />
                          <Route
                            path='/employees/salary'
                            element={
                              <Navigate to='/commission/salary' replace />
                            }
                          />
                          <Route
                            path='/employees/performance-commission'
                            element={
                              <Navigate to='/commission/performance' replace />
                            }
                          />
                          <Route path='/coupons' element={<CouponPage />} />
                          <Route
                            path='/employees/:businessId/:employeeId'
                            element={<EmployeeDetail />}
                          />
                          <Route path='/settings' element={<SettingsPage />} />
                          <Route
                            path='/settings/:section'
                            element={<SettingsPage />}
                          />
                          <Route
                            path='/notifications'
                            element={<AllNotifications />}
                          />
                          <Route
                            path='/announcements'
                            element={<AnnouncementsPage />}
                          />
                          <Route
                            path='/recruitment-dashboard'
                            element={<RecruitmentDashboard />}
                          />
                          <Route
                            path='/recruitment-dashboard/:tab'
                            element={<RecruitmentDashboard />}
                          />
                          <Route
                            path='/applicants/:recruitmentId'
                            element={<Applicants />}
                          />
                          <Route
                            path='/products/:businessId/additional-services'
                            element={<AdditionalServicesPage />}
                          />
                          <Route
                            path='/shop-setting'
                            element={<ShopSetting />}
                          />
                          <Route path='/store' element={<StorePage />} />
                          <Route
                            path='/store/add'
                            element={<AddProductPage />}
                          />
                          <Route
                            path='/store/edit/:productId'
                            element={<AddProductPage />}
                          />
                          <Route path='/orders' element={<OrdersPage />} />
                          <Route
                            path='/value-added/*'
                            element={<ValueAddedManagementPage />}
                          />
                          <Route
                            path='/class-ticket/*'
                            element={<ClassTicketManagementPage />}
                          />
                          <Route path='/schedule' element={<SchedulePage />} />
                          <Route
                            path='/consent-form/*'
                            element={<ConsentFormManagementPage />}
                          />
                          <Route
                            path='/reviews'
                            element={<ReviewManagementPage />}
                          />
                          <Route
                            path='/commission/salary'
                            element={<EmployeeSalarySettingsPage />}
                          />
                          <Route
                            path='/commission/monthly-salary'
                            element={<MonthlySalarySummaryPage />}
                          />
                          <Route
                            path='/commission/allowance'
                            element={<AllowanceSettingsPage />}
                          />
                          <Route
                            path='/commission/performance'
                            element={<EmployeeCommissionSettingsPage />}
                          />
                          <Route
                            path='/commission/value-added'
                            element={<ValueAddedCommissionSettingsPage />}
                          />
                          <Route
                            path='/commission/product-sales'
                            element={<ProductSalesCommissionSettingsPage />}
                          />
                          <Route
                            path='/commission/class-ticket'
                            element={<ClassTicketCommissionSettingsPage />}
                          />
                          <Route
                            path='/commission/service-rules'
                            element={<ServiceCommissionSettingsPage />}
                          />
                          <Route
                            path='/value-added/commission'
                            element={
                              <Navigate to='/commission/value-added' replace />
                            }
                          />
                          <Route
                            path='/payment-records'
                            element={<PaymentRecordsPage />}
                          />
                          <Route
                            path='/checkout/*'
                            element={<CheckoutManagementPage />}
                          />
                          {/* 線上商店模組。方案與權限由各頁面自行檢查，
                              後端也會擋，前端只負責顯示友善的提示畫面 */}
                          <Route
                            path='/shop/orders'
                            element={<ShopOrdersPage />}
                          />
                          <Route
                            path='/shop/pickup'
                            element={<ShopPickupPage />}
                          />
                          <Route
                            path='/shop/products'
                            element={<ShopProductsPage />}
                          />
                          <Route
                            path='/shop/settings'
                            element={<ShopSettingsPage />}
                          />
                          <Route
                            path='/inventory/products'
                            element={<InventoryProductsPage />}
                          />
                          <Route
                            path='/inventory/products/add'
                            element={<AddInventoryProductPage />}
                          />
                          <Route
                            path='/inventory/products/edit/:productId'
                            element={<AddInventoryProductPage />}
                          />
                          <Route
                            path='/inventory/purchase'
                            element={<InventoryPurchasePage />}
                          />
                          <Route
                            path='/inventory/purchase-records'
                            element={<InventoryPurchaseRecordsPage />}
                          />
                          <Route
                            path='/inventory/public-use'
                            element={<InventoryPublicUsePage />}
                          />
                          <Route
                            path='/inventory/usage'
                            element={<InventoryUsagePage />}
                          />
                        </Routes>
                      </ContentWrapper>
                    </Layout>
                  </Layout>
                </PrivateRoute>
              }
            />
          </Routes>
        </Router>
      </AuthProvider>
    </AntApp>
  )
}

export default App
